/**
 * TownSystem -- Manages town data, vendor interactions, and NPC generation.
 *
 * Loads town definitions from towns.json. Provides methods for:
 * - Finding towns by region
 * - Generating vendor stock
 * - Generating tavern recruits
 * - Processing vendor transactions (buy, sell, craft, reforge, gamble, reset)
 *
 * @module TownSystem
 */

import eventBus from '../core/EventBus.js';
import dataStore from './DataLoader.js';
import lootSystem, { RARITY_ORDER } from './LootSystem.js';
import inventorySystem from './InventorySystem.js';
import townsData from '../data/towns.json';
import Character from '../entities/Character.js';

/** Sell price multiplier (player sells at 40% of buy price). */
const SELL_MULTIPLIER = 0.4;

/** Base item prices by rarity. */
const RARITY_PRICES = {
  normal: 25,
  magic: 75,
  rare: 200,
  unique: 500,
  legendary: 1000,
};

/** Crafting upgrade costs. */
const UPGRADE_COSTS = {
  normal_to_magic: 50,
  magic_to_rare: 200,
  rare_to_unique: 500,
};

/** Reforge cost multiplier by rarity. */
const REFORGE_COSTS = {
  magic: 50,
  rare: 150,
  unique: 400,
  legendary: 800,
};

/** Gamble costs. */
const GAMBLE_SLOT_COST = 100;
const GAMBLE_RARITY_COSTS = {
  magic: 200,
  rare: 500,
  unique: 1200,
};

/** Inn heal cost per party member. */
const INN_COST_PER_MEMBER = 10;

/** NPC rarity stat multipliers. */
const NPC_RARITY_MULTIPLIERS = {
  common: 1.0,
  uncommon: 1.05,
  rare: 1.10,
  legendary: 1.20,
};

/** NPC hire costs by rarity. */
const NPC_HIRE_COSTS = {
  common: 50,
  uncommon: 150,
  rare: 400,
  legendary: 1000,
};

/** NPC rarity weights. */
const NPC_RARITY_WEIGHTS = {
  common: 50,
  uncommon: 30,
  rare: 15,
  legendary: 5,
};

/** Name pools for NPC generation. */
const NPC_FIRST_NAMES = [
  'Aldric', 'Brenna', 'Cedric', 'Delia', 'Edric', 'Fiona', 'Gareth', 'Hilda',
  'Ivar', 'Jessa', 'Kael', 'Lyra', 'Magnus', 'Nessa', 'Osric', 'Petra',
  'Quinn', 'Rowan', 'Sigrid', 'Theron', 'Una', 'Voss', 'Wren', 'Xara',
  'Yorick', 'Zara', 'Axton', 'Bria', 'Corwin', 'Dahlia',
];

class TownSystem {
  constructor() {
    /** @type {object[]} All town definitions. */
    this._towns = townsData || [];

    /** @type {Map<string, object[]>} Cached tavern recruits per town. */
    this._tavernRecruits = new Map();

    /** @type {Map<string, object[]>} Cached shop stock per town. */
    this._shopStock = new Map();

    /** @type {object[]} Benched party members. */
    this._bench = [];
  }

  // ── Town Access ──────────────────────────────────

  /**
   * Get all towns.
   * @returns {object[]}
   */
  getAllTowns() {
    return [...this._towns];
  }

  /**
   * Get a town by ID.
   * @param {string} townId
   * @returns {object|null}
   */
  getTown(townId) {
    return this._towns.find(t => t.id === townId) || null;
  }

  /**
   * Get the town for a specific region.
   * @param {string} regionId
   * @returns {object|null}
   */
  getTownByRegion(regionId) {
    return this._towns.find(t => t.regionId === regionId) || null;
  }

  /**
   * Check if a town has a specific vendor type.
   * @param {string} townId
   * @param {string} vendorType
   * @returns {boolean}
   */
  hasVendor(townId, vendorType) {
    const town = this.getTown(townId);
    return town ? town.vendors.includes(vendorType) : false;
  }

  // ── Shop Vendor ──────────────────────────────────

  /**
   * Generate or get cached shop stock for a town.
   * @param {string} townId
   * @param {number} partyLevel
   * @returns {object[]}
   */
  getShopStock(townId, partyLevel) {
    const cacheKey = `${townId}_${partyLevel}`;
    if (this._shopStock.has(cacheKey)) {
      return this._shopStock.get(cacheKey);
    }

    const stock = [];
    const numItems = 8 + Math.floor(Math.random() * 5); // 8-12 items

    for (let i = 0; i < numItems; i++) {
      // Shop items tend toward normal/magic
      const rarity = Math.random() < 0.6 ? 'normal' : (Math.random() < 0.7 ? 'magic' : 'rare');
      const item = lootSystem.generateItem(partyLevel, rarity);
      if (item) {
        item.buyPrice = this.getItemBuyPrice(item);
        stock.push(item);
      }
    }

    this._shopStock.set(cacheKey, stock);
    return stock;
  }

  /**
   * Get the buy price for an item.
   * @param {object} item
   * @returns {number}
   */
  getItemBuyPrice(item) {
    const basePrice = RARITY_PRICES[item.rarity] || RARITY_PRICES.normal;
    const levelMul = 1 + (item.level - 1) * 0.15;
    return Math.max(5, Math.round(basePrice * levelMul));
  }

  /**
   * Get the sell price for an item (40% of buy price).
   * @param {object} item
   * @returns {number}
   */
  getItemSellPrice(item) {
    const buyPrice = item.buyPrice || this.getItemBuyPrice(item);
    return Math.max(1, Math.round(buyPrice * SELL_MULTIPLIER));
  }

  /**
   * Buy an item from the shop.
   * @param {import('../entities/Party.js').default} party
   * @param {string} townId
   * @param {string} itemInstanceId
   * @returns {{ success: boolean, message: string }}
   */
  buyItem(party, townId, itemInstanceId) {
    const stock = this._shopStock.get(`${townId}_${party.partyLevel}`);
    if (!stock) return { success: false, message: 'Shop not loaded.' };

    const idx = stock.findIndex(i => i.instanceId === itemInstanceId);
    if (idx === -1) return { success: false, message: 'Item not in stock.' };

    const item = stock[idx];
    const price = item.buyPrice || this.getItemBuyPrice(item);

    if (party.gold < price) {
      return { success: false, message: 'Not enough gold.' };
    }

    if (!inventorySystem.addItem(item)) {
      return { success: false, message: 'Inventory is full.' };
    }

    party.gold -= price;
    stock.splice(idx, 1);

    eventBus.emit('town:buy', { item, price });
    eventBus.emit('inventory:changed');
    return { success: true, message: `Bought ${item.name} for ${price}g.` };
  }

  /**
   * Sell an item from inventory.
   * @param {import('../entities/Party.js').default} party
   * @param {string} itemInstanceId
   * @returns {{ success: boolean, message: string, gold: number }}
   */
  sellItem(party, itemInstanceId) {
    const item = inventorySystem.removeItem(itemInstanceId);
    if (!item) return { success: false, message: 'Item not found.', gold: 0 };

    const price = this.getItemSellPrice(item);
    party.gold += price;

    eventBus.emit('town:sell', { item, price });
    eventBus.emit('inventory:changed');
    return { success: true, message: `Sold ${item.name} for ${price}g.`, gold: price };
  }

  // ── Crafting Vendor ──────────────────────────────

  /**
   * Get the upgrade cost to increase an item's rarity by one tier.
   * @param {object} item
   * @returns {{ cost: number, targetRarity: string }|null}
   */
  getUpgradeCost(item) {
    const rarityIdx = RARITY_ORDER.indexOf(item.rarity);
    if (rarityIdx === -1 || rarityIdx >= 2) return null; // Can only upgrade normal->magic->rare

    const key = `${item.rarity}_to_${RARITY_ORDER[rarityIdx + 1]}`;
    const cost = UPGRADE_COSTS[key];
    if (!cost) return null;

    return { cost, targetRarity: RARITY_ORDER[rarityIdx + 1] };
  }

  /**
   * Upgrade an item's rarity.
   * @param {import('../entities/Party.js').default} party
   * @param {string} itemInstanceId
   * @returns {{ success: boolean, message: string, item: object|null }}
   */
  upgradeItem(party, itemInstanceId) {
    const items = inventorySystem.getItems();
    const item = items.find(i => i.instanceId === itemInstanceId);
    if (!item) return { success: false, message: 'Item not found.', item: null };

    const upgrade = this.getUpgradeCost(item);
    if (!upgrade) return { success: false, message: 'Item cannot be upgraded further.', item: null };

    if (party.gold < upgrade.cost) {
      return { success: false, message: 'Not enough gold.', item: null };
    }

    party.gold -= upgrade.cost;

    // Re-generate with higher rarity
    const newItem = lootSystem.generateItem(item.level, upgrade.targetRarity, item.slot);
    newItem.instanceId = item.instanceId; // Keep same instance ID

    // Replace in inventory
    inventorySystem.removeItem(item.instanceId);
    inventorySystem.addItem(newItem);

    eventBus.emit('town:craft', { oldItem: item, newItem, cost: upgrade.cost });
    return { success: true, message: `Upgraded to ${newItem.name}!`, item: newItem };
  }

  // ── Reforge Vendor ───────────────────────────────

  /**
   * Get the reforge cost for an item.
   * @param {object} item
   * @returns {number}
   */
  getReforgeCost(item) {
    return REFORGE_COSTS[item.rarity] || 100;
  }

  /**
   * Generate 3 reforge options for an item.
   * @param {object} item
   * @returns {object[]} Array of 3 possible affix replacements
   */
  getReforgeOptions(item) {
    if (!item.affixes || item.affixes.length === 0) return [];

    const options = [];
    for (let i = 0; i < 3; i++) {
      const newItem = lootSystem.generateItem(item.level, item.rarity, item.slot);
      if (newItem.affixes && newItem.affixes.length > 0) {
        // Pick a random affix from the generated item
        const affix = newItem.affixes[Math.floor(Math.random() * newItem.affixes.length)];
        options.push(affix);
      }
    }

    return options;
  }

  /**
   * Apply a reforge option to an item.
   * @param {import('../entities/Party.js').default} party
   * @param {string} itemInstanceId
   * @param {number} affixIndex - Index of the affix to replace
   * @param {object} newAffix - The new affix to apply
   * @returns {{ success: boolean, message: string }}
   */
  reforgeItem(party, itemInstanceId, affixIndex, newAffix) {
    const items = inventorySystem.getItems();
    const item = items.find(i => i.instanceId === itemInstanceId);
    if (!item) return { success: false, message: 'Item not found.' };

    const cost = this.getReforgeCost(item);
    if (party.gold < cost) {
      return { success: false, message: 'Not enough gold.' };
    }

    if (!item.affixes || affixIndex >= item.affixes.length) {
      return { success: false, message: 'Invalid affix index.' };
    }

    party.gold -= cost;
    item.affixes[affixIndex] = newAffix;

    eventBus.emit('town:reforge', { item, cost });
    eventBus.emit('inventory:changed');
    return { success: true, message: `Reforged ${item.name}!` };
  }

  // ── Gambler Vendor ───────────────────────────────

  /**
   * Gamble by slot type.
   * @param {import('../entities/Party.js').default} party
   * @param {string} slotType
   * @returns {{ success: boolean, message: string, item: object|null }}
   */
  gambleBySlot(party, slotType) {
    if (party.gold < GAMBLE_SLOT_COST) {
      return { success: false, message: 'Not enough gold.', item: null };
    }

    party.gold -= GAMBLE_SLOT_COST;
    const item = lootSystem.generateItem(party.partyLevel, undefined, slotType);

    if (!inventorySystem.addItem(item)) {
      party.gold += GAMBLE_SLOT_COST; // Refund
      return { success: false, message: 'Inventory is full.', item: null };
    }

    eventBus.emit('town:gamble', { item, cost: GAMBLE_SLOT_COST });
    return { success: true, message: `Received ${item.name}!`, item };
  }

  /**
   * Gamble by minimum rarity.
   * @param {import('../entities/Party.js').default} party
   * @param {string} minRarity
   * @returns {{ success: boolean, message: string, item: object|null }}
   */
  gambleByRarity(party, minRarity) {
    const cost = GAMBLE_RARITY_COSTS[minRarity] || GAMBLE_RARITY_COSTS.magic;
    if (party.gold < cost) {
      return { success: false, message: 'Not enough gold.', item: null };
    }

    party.gold -= cost;
    const item = lootSystem.generateItem(party.partyLevel, minRarity);

    if (!inventorySystem.addItem(item)) {
      party.gold += cost; // Refund
      return { success: false, message: 'Inventory is full.', item: null };
    }

    eventBus.emit('town:gamble', { item, cost });
    return { success: true, message: `Received ${item.name}!`, item };
  }

  /**
   * Get gamble costs.
   * @returns {{ slotCost: number, rarityCosts: object }}
   */
  getGambleCosts() {
    return {
      slotCost: GAMBLE_SLOT_COST,
      rarityCosts: { ...GAMBLE_RARITY_COSTS },
    };
  }

  // ── Reset Vendor ─────────────────────────────────

  /**
   * Get skill reset cost.
   * @param {import('../entities/Character.js').default} character
   * @returns {number}
   */
  getSkillResetCost(character) {
    return 100 * character.level;
  }

  /**
   * Get attribute reset cost.
   * @param {import('../entities/Character.js').default} character
   * @returns {number}
   */
  getAttributeResetCost(character) {
    return 50 * character.level;
  }

  /**
   * Reset a character's skill points.
   * @param {import('../entities/Party.js').default} party
   * @param {import('../entities/Character.js').default} character
   * @returns {{ success: boolean, message: string }}
   */
  resetSkills(party, character) {
    const cost = this.getSkillResetCost(character);
    if (party.gold < cost) {
      return { success: false, message: 'Not enough gold.' };
    }

    party.gold -= cost;

    // Count total skill points spent
    let totalPoints = 0;
    for (const [, level] of character.skills) {
      totalPoints += level;
    }

    character.skills.clear();
    character.unspentSkillPoints += totalPoints;

    eventBus.emit('town:reset-skills', { character, cost });
    return { success: true, message: `Reset skills for ${character.name}. Refunded ${totalPoints} skill points.` };
  }

  /**
   * Reset a character's attribute allocation.
   * @param {import('../entities/Party.js').default} party
   * @param {import('../entities/Character.js').default} character
   * @returns {{ success: boolean, message: string }}
   */
  resetAttributes(party, character) {
    const cost = this.getAttributeResetCost(character);
    if (party.gold < cost) {
      return { success: false, message: 'Not enough gold.' };
    }

    party.gold -= cost;

    // Calculate how many attribute points were spent (levels * 2 points per level)
    const pointsFromLevels = (character.level - 1) * 2;

    // Get race base attributes
    const race = dataStore.getById('races', character.raceId);
    const raceBase = race ? race.baseAttributes : { str: 10, agi: 10, int: 10, vit: 10, wis: 10, cha: 10 };
    const raceBonuses = race ? race.attributeBonuses : { str: 0, agi: 0, int: 0, vit: 0, wis: 0, cha: 0 };

    // Reset attributes to base + race bonuses
    for (const attr of ['str', 'agi', 'int', 'vit', 'wis', 'cha']) {
      character.attributes[attr] = (raceBase[attr] || 10) + (raceBonuses[attr] || 0);
    }

    character.unspentAttributePoints += pointsFromLevels;

    // Recalculate HP/mana
    character.hp = Math.min(character.hp, character.maxHp);
    character.mana = Math.min(character.mana, character.maxMana);

    eventBus.emit('town:reset-attributes', { character, cost });
    return { success: true, message: `Reset attributes for ${character.name}. Refunded ${pointsFromLevels} points.` };
  }

  // ── Inn ──────────────────────────────────────────

  /**
   * Get inn heal cost.
   * @param {import('../entities/Party.js').default} party
   * @returns {number}
   */
  getInnCost(party) {
    return INN_COST_PER_MEMBER * party.members.length;
  }

  /**
   * Rest at the inn. Heals all party members to full HP/mana.
   * @param {import('../entities/Party.js').default} party
   * @returns {{ success: boolean, message: string, healed: object[] }}
   */
  restAtInn(party) {
    const cost = this.getInnCost(party);
    if (party.gold < cost) {
      return { success: false, message: 'Not enough gold.', healed: [] };
    }

    party.gold -= cost;

    const healed = [];
    for (const member of party.members) {
      const hpBefore = member.hp;
      const manaBefore = member.mana;
      member.hp = member.maxHp;
      member.mana = member.maxMana;
      healed.push({
        name: member.name,
        hpBefore,
        hpAfter: member.hp,
        manaBefore,
        manaAfter: member.mana,
      });
    }

    eventBus.emit('town:rest', { cost, healed });
    return { success: true, message: `Party rested. All healed to full!`, healed };
  }

  // ── Tavern / NPC Generation ──────────────────────

  /**
   * Get or generate tavern recruits for a town.
   * @param {string} townId
   * @param {number} partyLevel
   * @returns {object[]}
   */
  getTavernRecruits(townId, partyLevel) {
    const cacheKey = `${townId}_${partyLevel}`;
    if (this._tavernRecruits.has(cacheKey)) {
      return this._tavernRecruits.get(cacheKey);
    }

    const recruits = [];
    const count = 3 + Math.floor(Math.random() * 3); // 3-5 recruits

    for (let i = 0; i < count; i++) {
      recruits.push(this._generateNPC(partyLevel));
    }

    this._tavernRecruits.set(cacheKey, recruits);
    return recruits;
  }

  /**
   * Refresh tavern recruits for a town.
   * @param {string} townId
   * @param {number} partyLevel
   * @returns {object[]}
   */
  refreshTavernRecruits(townId, partyLevel) {
    const cacheKey = `${townId}_${partyLevel}`;
    this._tavernRecruits.delete(cacheKey);
    return this.getTavernRecruits(townId, partyLevel);
  }

  /**
   * Hire a recruit from the tavern.
   * @param {import('../entities/Party.js').default} party
   * @param {string} townId
   * @param {number} recruitIndex
   * @returns {{ success: boolean, message: string, character: import('../entities/Character.js').default|null }}
   */
  hireRecruit(party, townId, recruitIndex) {
    const recruits = this._tavernRecruits.get(`${townId}_${party.partyLevel}`);
    if (!recruits || recruitIndex >= recruits.length) {
      return { success: false, message: 'Recruit not found.', character: null };
    }

    const recruit = recruits[recruitIndex];
    const cost = NPC_HIRE_COSTS[recruit.npcRarity] || NPC_HIRE_COSTS.common;

    if (party.gold < cost) {
      return { success: false, message: 'Not enough gold.', character: null };
    }

    if (party.members.length >= 4) {
      return { success: false, message: 'Party is full. Bench or dismiss a member first.', character: null };
    }

    party.gold -= cost;

    // Create a real Character from the recruit data
    const character = new Character({
      name: recruit.name,
      raceId: recruit.raceId,
      portrait: recruit.portrait,
      attributes: { ...recruit.attributes },
      skillTree1: recruit.skillTree1,
      skillTree2: recruit.skillTree2,
    });
    character.level = recruit.level;
    character.hp = character.maxHp;
    character.mana = character.maxMana;

    party.addMember(character);
    recruits.splice(recruitIndex, 1);

    eventBus.emit('town:hire', { character, cost });
    return { success: true, message: `Hired ${character.name} for ${cost}g!`, character };
  }

  /**
   * Bench a party member (move to inactive storage).
   * @param {import('../entities/Party.js').default} party
   * @param {number} memberIndex
   * @returns {{ success: boolean, message: string }}
   */
  benchMember(party, memberIndex) {
    if (party.members.length <= 1) {
      return { success: false, message: 'Cannot bench your last party member.' };
    }

    const member = party.removeMember(memberIndex);
    if (!member) return { success: false, message: 'Invalid member index.' };

    this._bench.push(member);
    eventBus.emit('town:bench', { character: member });
    return { success: true, message: `${member.name} moved to bench.` };
  }

  /**
   * Activate a benched member (move back to party).
   * @param {import('../entities/Party.js').default} party
   * @param {number} benchIndex
   * @returns {{ success: boolean, message: string }}
   */
  activateMember(party, benchIndex) {
    if (party.members.length >= 4) {
      return { success: false, message: 'Party is full.' };
    }

    if (benchIndex < 0 || benchIndex >= this._bench.length) {
      return { success: false, message: 'Invalid bench index.' };
    }

    const member = this._bench.splice(benchIndex, 1)[0];
    party.addMember(member);
    eventBus.emit('town:activate', { character: member });
    return { success: true, message: `${member.name} rejoined the party!` };
  }

  /**
   * Dismiss a party member permanently.
   * @param {import('../entities/Party.js').default} party
   * @param {number} memberIndex
   * @returns {{ success: boolean, message: string }}
   */
  dismissMember(party, memberIndex) {
    if (party.members.length <= 1) {
      return { success: false, message: 'Cannot dismiss your last party member.' };
    }

    const member = party.removeMember(memberIndex);
    if (!member) return { success: false, message: 'Invalid member index.' };

    eventBus.emit('town:dismiss', { character: member });
    return { success: true, message: `${member.name} has left the party.` };
  }

  /**
   * Get benched members.
   * @returns {object[]}
   */
  getBench() {
    return [...this._bench];
  }

  // ── NPC Generation (private) ─────────────────────

  /**
   * Generate a random NPC recruit.
   * @param {number} partyLevel
   * @returns {object}
   */
  _generateNPC(partyLevel) {
    // Pick random race
    const races = dataStore.getAll('races');
    const playableRaces = (races || []).filter(r => r.playable);
    const race = playableRaces.length > 0
      ? playableRaces[Math.floor(Math.random() * playableRaces.length)]
      : { id: 'human', name: 'Human', baseAttributes: { str: 10, agi: 10, int: 10, vit: 10, wis: 10, cha: 10 }, attributeBonuses: { str: 1, agi: 1, int: 0, vit: 0, wis: 0, cha: 2 } };

    // Pick random skill trees
    const skillTrees = dataStore.getAll('skill_trees') || [];
    const tree1 = skillTrees.length > 0 ? skillTrees[Math.floor(Math.random() * skillTrees.length)] : null;
    let tree2 = null;
    if (skillTrees.length > 1 && partyLevel >= 2) {
      const available = skillTrees.filter(t => t.id !== tree1?.id);
      tree2 = available.length > 0 ? available[Math.floor(Math.random() * available.length)] : null;
    }

    // Roll NPC rarity
    const npcRarity = this._rollNPCRarity();

    // Generate name
    const name = NPC_FIRST_NAMES[Math.floor(Math.random() * NPC_FIRST_NAMES.length)];

    // Base attributes = race base + race bonuses + some random allocation
    const attributes = { ...race.baseAttributes };
    const bonuses = race.attributeBonuses || {};
    for (const attr of ['str', 'agi', 'int', 'vit', 'wis', 'cha']) {
      attributes[attr] = (attributes[attr] || 10) + (bonuses[attr] || 0);
    }

    // Allocate some bonus points (simulate leveling)
    const bonusPoints = (partyLevel - 1) * 2;
    const attrKeys = ['str', 'agi', 'int', 'vit', 'wis', 'cha'];
    for (let i = 0; i < bonusPoints; i++) {
      const key = attrKeys[Math.floor(Math.random() * attrKeys.length)];
      attributes[key]++;
    }

    // Apply NPC rarity multiplier
    const multiplier = NPC_RARITY_MULTIPLIERS[npcRarity] || 1.0;
    if (multiplier !== 1.0) {
      for (const attr of attrKeys) {
        attributes[attr] = Math.round(attributes[attr] * multiplier);
      }
    }

    // Get class name
    let className = 'Adventurer';
    if (tree1) {
      className = tree2
        ? dataStore.getClassName(tree1.id, tree2.id)
        : dataStore.getClassName(tree1.id);
    }

    return {
      name,
      raceId: race.id,
      raceName: race.name,
      portrait: null,
      attributes,
      level: partyLevel,
      skillTree1: tree1?.id || null,
      skillTree2: tree2?.id || null,
      className,
      npcRarity,
      hireCost: NPC_HIRE_COSTS[npcRarity] || NPC_HIRE_COSTS.common,
    };
  }

  /**
   * Roll NPC rarity based on weights.
   * @returns {string}
   */
  _rollNPCRarity() {
    const rand = Math.random() * 100;
    let cumulative = 0;
    for (const [rarity, weight] of Object.entries(NPC_RARITY_WEIGHTS)) {
      cumulative += weight;
      if (rand < cumulative) return rarity;
    }
    return 'common';
  }

  // ── Cache Management ─────────────────────────────

  /**
   * Clear all caches (on leaving a town).
   */
  clearCaches() {
    this._shopStock.clear();
    // Keep tavern recruits cached (only refresh on explicit action)
  }

  /**
   * Clear everything.
   */
  clear() {
    this._shopStock.clear();
    this._tavernRecruits.clear();
    this._bench = [];
  }
}

const townSystem = new TownSystem();
export default townSystem;
export { RARITY_PRICES, SELL_MULTIPLIER, NPC_HIRE_COSTS, NPC_RARITY_MULTIPLIERS, INN_COST_PER_MEMBER };
